import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { statusHelper } from 'src/app/helpers';
import { userActions } from 'src/app/redux/actions';
import { nginrMessengerService, userService } from 'src/app/services';
import { reduxRootTypes } from 'src/types';
import DraggableModal from '../../DraggableModal/DraggableModal';
import {
  UserMailServerCredentialsFormProps,
  UpdateUserMailServerCredentialsFormType,
} from './userMailServerCredentialsForm.config';
import styles from './userMailServerCredentialsForm.module.scss';
import { errorLocals } from 'src/locals';
import useLogout from 'src/app/hooks/useLogout';
import { InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import { rsaEncryptBase64 } from 'src/app/utils/rsaEncrypt';
import { useTranslation } from 'react-i18next';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Tooltip from 'antd/es/tooltip';

const UserMailServerCredentialsForm: React.FC<UserMailServerCredentialsFormProps> = ({
  isModalOpen,
  setIsModalOpen,
  setOnClickMailServerCredentialsOpen,
  userDataInForm,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const { preferredLanguage } = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const dispatch = useDispatch();
  const logout = useLogout();

  async function getMessengerServerPublicKey(email: string) {
    return await nginrMessengerService.getPublicKey({
      user_mail_address: email,
    });
  }

  async function createMessengerUserPassword(email: string, password: string | undefined) {
    if (password) {
      return await nginrMessengerService.createUserPassword({
        user_mail_address: email,
        user_mail_password: password,
      });
    } else {
      throw new Error('Error while generating encrypted password');
    }
  }

  const onFormSave = async (formData: UpdateUserMailServerCredentialsFormType) => {
    const { email, password } = formData;

    try {
      const { data: PUBLIC_KEY } = await getMessengerServerPublicKey(email);

      const encryptedPassword = await rsaEncryptBase64(PUBLIC_KEY, password);

      const createUserPasswordResponse = await createMessengerUserPassword(formData.email, encryptedPassword);

      if (createUserPasswordResponse.data === 'OK') {
        statusHelper.displaySuccessStatusMessage('Successfully saved.');

        setIsModalOpen(false);

        form.resetFields();
      }
    } catch (error) {
      statusHelper.displayGenericError(error);
    }
  };

  const onNext = async () => {
    if (userState.isRegistered) {
      setIsModalOpen(false);
    } else {
      try {
        const userUpdateData = await userService.updateMeProfile({
          id: userState.id,
          isRegistered: true,
        });

        dispatch(userActions.setInformation(userUpdateData.data));
        setIsModalOpen(false);
      } catch (error: any) {
        if (error !== errorLocals.errorHandledError) {
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    }
  };

  const onCloseModal = () => {
    if (!userState.isRegistered) {
      logout();
    }
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (userDataInForm.email) {
      form.setFieldsValue({
        email: userDataInForm.email,
      });
    }
  }, [userDataInForm.email]);

  return (
    <>
      {isModalOpen && (
        <DraggableModal
          headerText={t('administration.users.enterMailServerCredentials')}
          onClickClose={onCloseModal}
          style={{ height: '25vh' }}
        >
          <Form
            onFinish={onFormSave}
            form={form}
            initialValues={{
              email: '',
              password: '',
            }}
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className={styles.container}>
                  <div className="popup-row grid-col-2-fixed">
                    <div className="popup-input-container">
                      <div className={styles.titleContainer}>
                        <span className="popup-input-title">{t('administration.ontologies.email')}</span>{' '}
                        <Tooltip
                          title={t('administration.users.provideEmailAddressLinkedWithMailServer')}
                          zIndex={9999}
                        >
                          <InfoCircleOutlined style={{ fontSize: 12 }} />
                        </Tooltip>
                      </div>
                      <Form.Item
                        name="email"
                        validateTrigger="onBlur"
                        rules={[{ required: true, type: 'email', message: t('errors.email') || '' }]}
                      >
                        <Input className={styles.standard_input} />
                      </Form.Item>
                    </div>
                    <div className="popup-input-container">
                      <div className={styles.titleContainer}>
                        <span className="popup-input-title">{t('administration.users.password')}</span>{' '}
                        <Tooltip title={t('administration.users.providePasswordOfYourEmailAccount')} zIndex={9999}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InfoCircleOutlined style={{ fontSize: 12, marginRight: 8 }} />
                            <LockOutlined />
                          </div>
                        </Tooltip>
                      </div>
                      <Form.Item
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t('general.required') || '',
                          },
                        ]}
                      >
                        <Input.Password
                          type="password"
                          className={styles.standard_input}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="popup-footer" style={{ marginRight: '6%' }}>
              <div>
                <button className="next-button" type="submit">
                  {t('general.save')}
                </button>
                <button className="modal-button" onClick={onNext} type="button">
                  {userState.isRegistered ? t('general.cancel') : t('general.next')}
                </button>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}
    </>
  );
};

export default UserMailServerCredentialsForm;
